import React, { useEffect, useState, useMemo } from 'react';
import {
  Button,
  FormGroup,
  Label,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  InputGroup,
  Input,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDealFunder,
  saveDealFunder,
  dealFunderClearDetails,
  redirectToDealFunder,
} from '../../actions/dealFunderActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { BeatLoader } from 'react-spinners';
import { DebounceInput } from 'react-debounce-input';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';

const DealFunderEditPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dealFunderId } = useParams();

  const details = useSelector((state) => state.dealFunder.details);
  const isLoading = useSelector((state) => state.dealFunder.isLoading);
  const isSaving = useSelector((state) => state.dealFunder.isSaving);
  const redirectId = useSelector((state) => state.dealFunder.redirectId);

  const [id, setId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isGlobal, setIsGlobal] = useState(true);
  const [defaultRegions, setDefaultRegions] = useState([]);
  const [customRegions, setCustomRegions] = useState([]);
  const [isNew, setIsNew] = useState(true);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState('');
  const [editRowId, setEditRowId] = useState(null);
  const [editRegionCode, setEditRegionCode] = useState('');
  const [editRegionName, setEditRegionName] = useState('');
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    if (dealFunderId) {
      dispatch(getDealFunder(dealFunderId));
      setIsNew(false);
    } else {
      dispatch(dealFunderClearDetails());
      setIsNew(true);
      setId('');
      setDisplayName('');
      setIsGlobal(true);
      setDefaultRegions([]);
      setCustomRegions([]);
    }
  }, [dispatch, dealFunderId]);

  useEffect(() => {
    if (details && Object.keys(details).length !== 0) {
      setIsNew(details.isNew || false);
      setId(details.id || '');
      setDisplayName(details.name || '');
      setIsGlobal(details.isGlobalRegionType || false);
      setDefaultRegions(details.defaultRegions || []);
      setCustomRegions(details.customRegions || []);
    } else {
      setIsNew(true);
      setId('');
      setDisplayName('');
      setIsGlobal(true);
      setDefaultRegions([]);
      setCustomRegions([]);
    }
  }, [details]);

  useEffect(() => {
    if (typeof redirectId === 'string' && redirectId) {
      dispatch(redirectToDealFunder({}));
      navigate(`/dealFunder/${redirectId}`);
    }
  }, [redirectId, dispatch, navigate]);

  const handleRegionType = (newValue) => {
    setIsGlobal(newValue === 'true' || newValue === true);
  };

  const handleDisplayNameChanged = (e) => setDisplayName(e.target.value);
  const handleIdChanged = (e) => setId(e.target.value);

  const minLength = (value, length, label) => {
    if (!value || value.length < length) {
      return `${label} must be at least ${length} characters long`;
    }
  };

  const addError = (message, fieldName, errorsObj) => {
    if (message) {
      errorsObj = errorsObj || {};
      errorsObj[fieldName] = message;
    }
    return errorsObj;
  };

  const validate = (model) => {
    let errorsObj = {};
    errorsObj = addError(minLength(model.id, 2, 'Id'), 'id', errorsObj);
    errorsObj = addError(
      minLength(model.displayName, 2, 'Display Name'),
      'displayName',
      errorsObj,
    );
    return {
      isValid: Object.keys(errorsObj).length === 0,
      errors: errorsObj,
    };
  };

  const save = (e) => {
    e.preventDefault();
    setErrors({});
    const model = {
      id,
      displayName,
      isGlobalRegionType: isGlobal,
      regions: customRegions,
    };
    const result = validate(model);
    if (result.isValid) {
      dispatch(
        saveDealFunder(isNew, model, {
          id,
          name: displayName,
          isGlobalRegionType: isGlobal,
          defaultRegions,
          customRegions,
        }),
      );
    } else {
      setErrors(result.errors);
    }
  };

  // Table Data
  const data = useMemo(() => {
    const regions = isGlobal ? defaultRegions : customRegions;
    if (!search) return regions;
    return regions.filter(
      (r) =>
        r.regionCode?.toLowerCase().includes(search.toLowerCase()) ||
        r.regionName?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [isGlobal, defaultRegions, customRegions, search]);

  // Table Columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'regionCode',
        header: 'Code',
        cell: (info) =>
          editRowId === info.row.original.id ? (
            <input
              className='form-control form-control-sm'
              value={editRegionCode}
              onChange={(e) => setEditRegionCode(e.target.value)}
            />
          ) : (
            info.getValue()
          ),
      },
      {
        accessorKey: 'regionName',
        header: 'Display Name',
        cell: (info) =>
          editRowId === info.row.original.id ? (
            <input
              className='form-control form-control-sm'
              value={editRegionName}
              onChange={(e) => setEditRegionName(e.target.value)}
            />
          ) : (
            info.getValue()
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editRowId,
      editRegionCode,
      editRegionName,
      isGlobal,
      defaultRegions,
      customRegions,
    ],
  );

  const [pageSize, setPageSize] = useState(25); // De
  const paginatedData = useMemo(() => {
    const regions = isGlobal ? defaultRegions : customRegions;
    if (!search)
      return regions.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
    const filtered = regions.filter(
      (r) =>
        r.regionCode?.toLowerCase().includes(search.toLowerCase()) ||
        r.regionName?.toLowerCase().includes(search.toLowerCase()),
    );
    return filtered.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobal, defaultRegions, customRegions, search, pageIndex]);

  const totalPages = Math.ceil(
    (isGlobal ? defaultRegions : customRegions).filter(
      (r) =>
        r.regionCode?.toLowerCase().includes(search.toLowerCase()) ||
        r.regionName?.toLowerCase().includes(search.toLowerCase()),
    ).length / pageSize,
  );

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
  };

  // Table Instance
  const table = useReactTable({
    data: paginatedData,
    columns,
    state: { pagination: { pageIndex, pageSize } },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1; // 1-based

  const totalButtons = 5; // Number of page buttons to show
  let pageButtons = [];

  if (pageCount <= totalButtons) {
    pageButtons = [...Array(pageCount)].map((_, i) => i + 1);
  } else if (currentPage <= Math.ceil(totalButtons / 2)) {
    // Show first 5 pages
    pageButtons = [...Array(totalButtons)].map((_, i) => i + 1);
  } else if (currentPage >= pageCount - Math.floor(totalButtons / 2)) {
    // Show last 5 pages
    pageButtons = [...Array(totalButtons)].map(
      (_, i) => pageCount - totalButtons + 1 + i,
    );
  } else {
    // Center current page
    const start = currentPage - Math.floor(totalButtons / 2);
    pageButtons = [...Array(totalButtons)].map((_, i) => start + i);
  }

  if (isLoading) {
    return <BeatLoader color={'#004C5F'} />;
  }

  return (
    <div className='deal-funder'>
      <Row>
        <Col md={4}>
          <Card>
            <CardHeader>
              <h4>Details</h4>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label for='id'>Id</Label>
                <DebounceInput
                  className='form-control'
                  debounceTimeout={300}
                  disabled={!isNew}
                  type='text'
                  name='id'
                  id='id'
                  maxLength='32'
                  value={id}
                  onChange={handleIdChanged}
                />
                {errors.id && (
                  <div className='validation-error'>{errors.id}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for='displayName'>Display Name</Label>
                <DebounceInput
                  className='form-control'
                  debounceTimeout={300}
                  type='text'
                  name='displayName'
                  id='displayName'
                  value={displayName}
                  onChange={handleDisplayNameChanged}
                />
                {errors.displayName && (
                  <div className='validation-error'>{errors.displayName}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for='regionType'>Region Type</Label>
                <InputGroup className='radio'>
                  <Label>
                    <Input
                      name='regionType'
                      type='radio'
                      checked={isGlobal}
                      onChange={handleRegionType}
                    />{' '}
                    Global
                  </Label>
                  <Label>
                    <Input
                      name='regionType'
                      type='radio'
                      checked={!isGlobal}
                      onChange={handleRegionType}
                    />{' '}
                    Funder
                  </Label>
                </InputGroup>
              </FormGroup>
              <FormGroup className='buttons-panel'>
                <Button color='primary' onClick={save}>
                  <FontAwesomeIcon icon={faSave} /> Save
                </Button>
                <Link to='/dealFunder' className='btn btn-secondary'>
                  Back
                </Link>
              </FormGroup>
            </CardBody>
          </Card>
          {isSaving && <BeatLoader color={'#004C5F'} />}
        </Col>
        <Col md={8}>
          <Card>
            <CardHeader>
              <h4>Regions</h4>
            </CardHeader>
            <CardBody>
              <Row className='mb-2 d-flex justify-content-end align-items-center p-0'>
                <Col xs={6} sm={6} md={6} lg={4}>
                  <input
                    className='form-control'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder='Search regions...'
                    name='search'
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <table className='table table-bordered table-blue'>
                    <thead className='table-primary'>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              className='text-dark fw-bold align-middle column-header'
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={columns.length}
                            style={{ backgroundColor: '#fff' }}
                          >
                            Global regions will be applied after save
                          </td>
                        </tr>
                      ) : (
                        table.getRowModel().rows.map((row) => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <td key={cell.id}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Col>
              </Row>
              {/* Pagination */}
              {!isLoading && table.getRowModel().rows.length > 0 && (
                <nav aria-label='Page navigation' className='mt-3'>
                  <div className='d-flex align-items-center justify-content-between'>
                    {/* Page size dropdown on the left */}
                    <div>
                      <select
                        className='btn btn-default btn-secondary dropdown-toggle'
                        value={pageSize}
                        name='selectPageSize'
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPageIndex(0); // Reset to first page when page size changes
                        }}
                      >
                        {[10, 25, 30, 50].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Pagination controls on the right */}
                    <ul className='pagination mb-0'>
                      {/* First and Previous */}
                      {currentPage > 3 && (
                        <li>
                          <button
                            className='page-link'
                            onClick={() => setPageIndex(0)}
                            disabled={currentPage === 1}
                          >
                            {'<<'}
                          </button>
                        </li>
                      )}
                      {currentPage > 1 && (
                        <li>
                          <button
                            className='page-link'
                            onClick={() => setPageIndex(currentPage - 2)}
                            disabled={currentPage === 1}
                          >
                            {'<'}
                          </button>
                        </li>
                      )}
                      {/* Page numbers */}
                      {pageButtons.map((page) => (
                        <li
                          key={page}
                          className={`page-item${currentPage === page ? ' active' : ''}`}
                        >
                          <button
                            className='page-link'
                            onClick={() => setPageIndex(page - 1)}
                          >
                            {page}
                          </button>
                        </li>
                      ))}
                      {/* Next and Last */}
                      {currentPage < pageCount && (
                        <li>
                          <button
                            className='page-link'
                            onClick={() => setPageIndex(currentPage)}
                            disabled={currentPage === pageCount}
                          >
                            {'>'}
                          </button>
                        </li>
                      )}
                      {currentPage < pageCount - 2 && (
                        <li>
                          <button
                            className='page-link'
                            onClick={() => setPageIndex(pageCount - 1)}
                            disabled={currentPage === pageCount}
                          >
                            {'>>'}
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </nav>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DealFunderEditPage;
